/**
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import isEmpty from 'lodash.isempty';
import {PageTitle} from '@ion/applications/src/pages/admin/AdminContainer';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {GenericObject} from '@ion/core/src/types/entities';
import {
  isArrayEmpty
} from '@ion/core/src/utils/ArrayUtil';
import {
  safeJSONStringify
} from '@ion/core/src/utils/JSONUtils';
import {
  normalize
} from '@ion/core/src/utils/StringUtil';
import {
  ArgumentTypes,
  CallbackArgumentsType,
  ColumnFormatType,
  ColumnWithOrderKey,
  MAX_LENGTH,
  PREFERENCE_NAME,
  SavedColumnType
} from './types';
import {checkIsNameExist, getColumnInRequiredFormat} from './utils';

export interface useSavedColumnViewTypes {
  selectedColumns?: ColumnWithOrderKey[] | null;
  pageName?: PageTitle
};

export const getSavedColumnsForPage = (pageName): SavedColumnType | GenericObject => {
  const savedColumns = UserPreferences.get(PREFERENCE_NAME) ?? [];
  let savedColumnsForPage = {};
  if (!isArrayEmpty(savedColumns)) {
    savedColumnsForPage = savedColumns.filter(({pageName: name}: SavedColumnType) => name === pageName)[0] ?? {};
  }
  return savedColumnsForPage;
};

export const callbackArgumnets: CallbackArgumentsType = {
  [ArgumentTypes.IS_NAME_EXIST]   : false,
  [ArgumentTypes.iS_MORETHAN_MAX] : false,
  [ArgumentTypes.iS_SUCCESS]      : false
};

/**
 * @useSavedColumnView
 *
 * Provides a Hook which returns onSave method which will be call when we click on Done button.
 * onSave method take two arguments first is save column name and second one is callback function.
 *
 * @param selectedColumns It is an array of selected columns.
 * @param pageName It is current page name.
 * @returns It returns loading and onSave method which will be call when we click on Done button.
 *
 * ```typescript
 *
 *     const {onSave, loading} = useSavedColumnView({selectedColumns});
 *
 *     ...
 *
 *    const onSubmit = useCallback(function() {
 *      onSave(savedColumnName, checkStatusOnSave);
 *    }, []);
 *
 *    <SaveButton ...
 *      onSave={onSubmit}
 *      disabled={!normalize(savedColumnName) || loading}
 *      loading={loading}
 *      ... />
 *
 * ```
 * @see SaveColumnEditor
 */
export const useSavedColumnView = ({
  selectedColumns,
  pageName
}: useSavedColumnViewTypes) => {
  const [columnFormat, setColumnFormat] = useState<ColumnFormatType[]>([]);
  const [loading, setLoading]           = useState(false);

  // Get the columns item in required format.
  useEffect(() => {
    if (selectedColumns?.length) {
      const columns = getColumnInRequiredFormat(selectedColumns);
      setColumnFormat(columns);
    }
  }, [selectedColumns]);

  /**
   * Save the selected column with a given name.
   */
  const onSave = useCallback(async function(savedColumnName: string, callback) {
    const name         = normalize(savedColumnName || '');
    const savedColumns = UserPreferences.get(PREFERENCE_NAME) ?? [];
    const savedColumnsForPage: SavedColumnType | GenericObject = getSavedColumnsForPage(pageName);
    if (columnFormat?.length > 0) {
      if (isEmpty(savedColumnsForPage)) {
        savedColumnsForPage.pageName = pageName;
        savedColumnsForPage.createdAt = new Date();
        savedColumnsForPage.views = [];
      } else if (savedColumnsForPage.views?.length >= MAX_LENGTH) {
        callback?.({...callbackArgumnets, isMorethanMax: true});
        return;
      } else if (checkIsNameExist(savedColumnsForPage?.views, name)) {
        callback?.({...callbackArgumnets, isNameExist: true});
        return;
      }
      savedColumnsForPage.selectedViewName = name;
      savedColumnsForPage.isNotAllowToCreate = false;
      savedColumnsForPage.views.push({
        viewName : name,
        columns  : safeJSONStringify(columnFormat)
      });
      const restSavedColumns = savedColumns?.filter(
        (page: SavedColumnType) => page.pageName !== pageName);
      setLoading(true);
      await UserPreferences.set(
        PREFERENCE_NAME,
        [...restSavedColumns, savedColumnsForPage],
        !UserPreferences.hasChangeDetection()
      );
      setLoading(false);
      callback?.({...callbackArgumnets, isSuccess: true});
    }
  }, [columnFormat]);

  return {
    onSave,
    loading
  };
};

export default useSavedColumnView;
